import { setlist } from '@/utils/typesense';
import { type QueryClient, useQuery } from '@tanstack/react-query';
import { type GetCarouselsForSlugParams, getPageCarouselSearchParameters as buildPageCarouselSearchParameters } from '@music-vine/setlist';
import { parseCarousels, parseCarouselsCombined } from './parse-carousels';
import type { TypesenseCarouselProcessedResult, useTypesenseCarouselsForPageProps, useTypesenseCarouselsProps } from './types';
import type { TypesenseCarousel } from '@music-vine/setlist';
import { HYDRATED_LISTING_STALE_TIME_MS } from '@/constants/query-freshness';
import type { CategoryHeader } from '@/utils/pages/api';
import type { DebugInfoReadoutItem } from '@/components/DebugInfoReadout';
import { isCollection } from '@/lib/page-is-collection';
import type { Collection } from '@/types/collection';
import { searchKeys } from '@/utils/queryKeys';
import { type CarouselSearchDebugOverride, defaultOverrideCarouselSearchState } from '@/atoms/debug';
const parseDebugData = (pageData: CategoryHeader | Collection, currentDebugItems: DebugInfoReadoutItem[]): DebugInfoReadoutItem[] => {
  const isCollectionPage = isCollection(pageData);
  const additionalDebugItems: DebugInfoReadoutItem[] = [{
    title: 'Page',
    value: `${pageData.primary_heading} (${isCollectionPage ? '' : pageData.url})`
  }, {
    title: 'Query',
    value: isCollectionPage || pageData.search_term === '' ? '(No search term provided, automated carousels disabled)' : pageData.search_term
  }, {
    title: 'Excluded keywords',
    value: isCollectionPage || pageData.excluded_keywords === '' ? '(None)' : pageData.excluded_keywords
  },
  // Added as a visual separator
  {
    title: '-',
    value: '-'
  }];
  return additionalDebugItems.concat(currentDebugItems);
};

// Builds the two Typesense multi-search parameter objects (manual page
// carousels + automatic vector-search carousels) for a page. Shared between the
// client hook and the SSR prefetch so their query keys stay identical.
// Exported for the SSR/client query-key sync test.
export const getPageCarouselSearchParameters = (library: 'music' | 'sfx', pageData: CategoryHeader | Collection, carouselSearchDebugOverride: CarouselSearchDebugOverride) => {
  const isCollectionPage = isCollection(pageData);
  const {
    carouselDebugInfoVisible,
    overrideCarouselExcludes
  } = carouselSearchDebugOverride;
  return buildPageCarouselSearchParameters({
    library,
    name: pageData.name,
    url: isCollectionPage ? '' : pageData.url,
    searchTerm: !isCollectionPage && pageData.search_term ? pageData.search_term : '',
    excludedKeywords: carouselDebugInfoVisible && overrideCarouselExcludes !== '' ? overrideCarouselExcludes : !isCollectionPage && pageData.excluded_keywords,
    showDebugResults: carouselDebugInfoVisible
  });
};
const fetchPageCarousels = async (pageData: CategoryHeader | Collection, pageSearchParameters: GetCarouselsForSlugParams['searchParameters'], autoSearchParameters: GetCarouselsForSlugParams['searchParameters']): Promise<TypesenseCarouselProcessedResult> => {
  const response = await setlist.getCarouselsMulti(pageSearchParameters, autoSearchParameters);
  const processedResult = parseCarouselsCombined(response.results);

  // Add additional debug items before existing items
  processedResult.debug_info = parseDebugData(pageData, processedResult.debug_info);
  return processedResult;
};
export const useTypesenseCarouselsForPage = ({
  library,
  pageData,
  queryOptions,
  carouselSearchDebugOverride
}: useTypesenseCarouselsForPageProps) => {
  const {
    pageSearchParameters,
    autoSearchParameters
  } = getPageCarouselSearchParameters(library, pageData, carouselSearchDebugOverride);
  const useQueryResult = useQuery({
    queryKey: searchKeys.pageCarousels(pageSearchParameters, autoSearchParameters),
    queryFn: () => fetchPageCarousels(pageData, pageSearchParameters, autoSearchParameters),
    // Same hydration story as the grids: `prefetchPageCarousels` ships these in
    // `dehydratedState`, and the app-wide default `staleTime` of 0 made the
    // client refetch them on mount anyway (DEV-6490).
    staleTime: HYDRATED_LISTING_STALE_TIME_MS,
    ...queryOptions
  });
  return useQueryResult;
};

// Prefetches page carousels server-side so they hydrate on first paint and
// don't "flash" in after a client fetch. The debug override is read from a
// storage-backed atom that always starts at its debug-off default on first
// client render, so SSR must use that same default — otherwise the query key
// would differ and the prefetched data wouldn't hydrate the client query.
export const prefetchPageCarousels = async (queryClient: QueryClient, library: 'music' | 'sfx', pageData: CategoryHeader | Collection) => {
  const {
    pageSearchParameters,
    autoSearchParameters
  } = getPageCarouselSearchParameters(library, pageData, defaultOverrideCarouselSearchState);
  await queryClient.prefetchQuery({
    queryKey: searchKeys.pageCarousels(pageSearchParameters, autoSearchParameters),
    queryFn: () => fetchPageCarousels(pageData, pageSearchParameters, autoSearchParameters)
  });
};
export const useTypesenseCarousels = ({
  searchParameters,
  queryOptions
}: useTypesenseCarouselsProps = {}) => {
  const useQueryResult = useQuery({
    queryKey: searchKeys.carousels(searchParameters),
    queryFn: async () => {
      const response = await setlist.getCarousels({
        searchParameters
      });
      const carousels: TypesenseCarousel[] = parseCarousels(response);
      return carousels;
    },
    ...queryOptions
  });
  return useQueryResult;
};